import { useEffect } from "react";
import { X } from "lucide-react";
import type { AdCampaign, AdTriggerEvent } from "@/types";
import { isSafeUrl, logImpression } from "@/lib/adEngine";
import { trackEvent } from "@/lib/audit";
import { useZeroObstruction } from "@/lib/pr22/zeroObstruction";
import { MinimalSponsorBadge } from "@/components/match/MinimalSponsorBadge";
import { useClaimAttention } from "@/hooks/useAttentionFocus";

interface Props {
  campaign: AdCampaign | null;
  event: AdTriggerEvent | null;
  onClose: () => void;
}

export function RadarSmartAd({ campaign, event, onClose }: Props) {
  const { cleared } = useZeroObstruction();
  // Master attention — sponsor takeover at prio 75 (between prediction and viral).
  // Short TTL: emotional surfaces/ticker/poll/prediction yield only briefly.
  useClaimAttention("sponsor", !!campaign && !!event, 6_000);
  useEffect(() => {
    if (!campaign || !event) return;
    void trackEvent("ad_impression", {
      campaign_id: campaign.id,
      sponsor: campaign.sponsor_name,
      event,
    });
    void logImpression({ campaign_id: campaign.id, event_type: event });
  }, [campaign?.id, event]);

  if (!campaign || !event) return null;

  // PR-22 — during critical plays, swap the full ad card for a 32px badge
  // so the radar and ball trajectory stay fully visible.
  if (cleared) return <MinimalSponsorBadge campaign={campaign} />;

  const safe = isSafeUrl(campaign.landing_url);

  const handleClick = () => {
    void trackEvent("ad_click", {
      campaign_id: campaign.id,
      sponsor: campaign.sponsor_name,
      event,
    });
    void logImpression({ campaign_id: campaign.id, event_type: event, clicked: true });
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void trackEvent("ad_closed", { campaign_id: campaign.id });
    onClose();
  };

  const inner = (
    <div className="group relative flex items-center gap-3 rounded-xl border border-border bg-[oklch(0.06_0.005_260)] px-3.5 py-3 text-[oklch(1_0_0)] shadow-lg transition hover:border-[oklch(0.82_0.24_145)]">
      <span className="absolute -top-2 left-2 rounded-full bg-[oklch(0.82_0.24_145)] px-2 py-[2px] text-[9px] font-black tracking-widest text-background shadow">
        PATROCINADO
      </span>
      {campaign.logo_url && isSafeUrl(campaign.logo_url) && (
        <img
          src={campaign.logo_url}
          alt={campaign.sponsor_name}
          className="h-9 w-9 rounded-md bg-white object-contain p-0.5 shadow-sm"
          loading="lazy"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="text-[13px] font-black tracking-wide text-[oklch(1_0_0)]">{campaign.sponsor_name}</span>
        {campaign.tagline && (
          <span className="text-[13px] font-bold leading-snug text-[oklch(1_0_0)] sm:max-w-[220px]">
            {campaign.tagline}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Cerrar anuncio"
        className="ml-1 rounded-full p-1 text-[oklch(1_0_0)] hover:bg-white/10"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );

  // Safe zone: keep below the narrative ticker (top-3, h≈28px) and never
  // overlap the emotional signal overlay on the left. Constrain width on
  // mobile so the card never bleeds into the scoreboard or HUD.
  return (
    <div className="pointer-events-auto w-full sm:max-w-[280px] animate-in fade-in slide-in-from-top-1 duration-500">
      {safe ? (
        <a
          href={campaign.landing_url!}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className="block"
        >
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
}